/**
 * How much of the account the strategy may use, and how much of that it has.
 *
 * This was a card called "capital by calculation" that said, at the bottom, that
 * it was not in force. It is in force now: the same schedule decides how many
 * slots run, so a figure here is the reason a slot exists rather than a note
 * about a formula somebody might adopt later.
 *
 * The share is read from the highest equity the account ever reached rather
 * than today's, so a drawdown lowers what is available without ever raising the
 * share allowed. Every number arrived measured; nothing here computes one.
 *
 * The bar is the only geometry: it says at a glance what four figures say
 * exactly, and it is drawn from the same two numbers printed beneath it.
 */
import type { ReactElement } from "react";
import { Stat } from "@/design-system/Stat";

import { CircleAlert } from "lucide-react";

import { AllocationBar } from "@/design-system/AllocationBar";
import { LimitBar } from "@/design-system/LimitBar";
import { poolColour } from "@/design-system/poolColour";

import type {
  CapitalResponse,
  OverviewResponse,
  PoolCapacityResponse,
} from "@/shared/api";
import { isNegative, money, percent, percentOf, signedMoney } from "@/shared/money/money";


/**
 * One pool as a window rather than as a row.
 *
 * Three pools, three questions each — how many slots, how much money, how it is
 * doing — is nine numbers. As four columns of small text they read as a
 * spreadsheet fragment: the eye has to hold the column heads while crossing.
 * As three cards each answering its own three questions, a pool is read in one
 * place and compared with its neighbours by position.
 */
function PoolCard({
  pool,
  index,
}: {
  readonly pool: PoolCapacityResponse;
  readonly index: number;
}): ReactElement {
  // A pool standing entirely empty is the one case a fraction cannot explain:
  // the market offered nothing worth entering, or the money has not reached it
  // yet. Everywhere else the numbers say enough on their own.
  const idle = pool.running === 0 && pool.paid === 0;
  const slots = `${String(pool.running)}/${String(pool.capacity)}`;
  return (
    <div
      data-testid={`pool-${pool.pool}`}
      className="flex min-w-0 flex-col rounded-panel border border-border-subtle bg-surface-default"
    >
      <div className="flex min-h-11 items-center justify-between gap-2 border-b border-border-subtle px-4">
        <span className="flex items-center gap-2 text-body-lg font-semibold text-text-primary">
          <span aria-hidden="true" className={`size-2 rounded-full ${poolColour(pool.pool, index)}`} />
          Пул {pool.pool}
        </span>
        <span className="rounded-row bg-surface-selected px-2 py-0.5 font-numeric text-helper-sm tabular-nums text-text-secondary">
          {slots} слотов
        </span>
      </div>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-1">
          <span className="text-helper-sm text-text-muted">Капитал, USDT</span>
          <span
            className="font-numeric text-metric-md tabular-nums text-text-primary"
            data-metric-id="allocation.pool_capital"
            data-metric-role="detail"
          >
            {idle ? "капитала нет" : money(pool.allocated)}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2 border-t border-border-subtle pt-3">
          <span className="text-helper-sm text-text-muted">Нереализованный PnL</span>
          <span
            className={`font-numeric text-body-main tabular-nums ${
              isNegative(pool.unrealised) ? "text-status-negative" : "text-text-secondary"
            }`}
            data-metric-id="allocation.pool_unrealised"
            data-metric-role="detail"
            title={pool.unrealised}
          >
            {pool.running === 0 ? "—" : signedMoney(pool.unrealised)}
          </span>
        </div>
        <LimitBar
          filled={percentOf(String(pool.running), String(pool.capacity))}
          label={`Занято слотов: ${slots}`}
        />
      </div>
    </div>
  );
}

export function Allocation({
  capital,
  overview,
}: {
  readonly capital: CapitalResponse | null | undefined;
  readonly overview: OverviewResponse;
}): React.JSX.Element | null {
  // Absent until the equity record has a high-water mark. A rate taken from
  // today's equity would behave backwards for as long as it stood, and an
  // empty block would invite reading zeros as measurements.
  if (!capital) return null;

  return (
    <div className="flex flex-col gap-3" data-testid="capital-plan">
      {/* The ceiling, the share of it in use and the room left under it moved
          to the lead strip of the screen, where they are the subject rather
          than a caption over a bar. One fact, one owner, per §8.6 — this panel
          now shows how the committed half divides, which is the one question
          a bar of this shape answers. */}
      <AllocationBar capital={capital} overview={overview} caption={false} />

      {/* The capital ladder, and only what a person can act on: where the
          migration is, how far it has got, what the next step costs and
          whether steps are being granted at all. Everything on it was measured
          on the server — the browser does not work out how much room the
          account has. */}
      {capital.scaling != null && (
        <section
          className="flex flex-col gap-2 rounded-panel border border-border-subtle p-3"
          data-testid="capital-scaling"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h3 className="text-label-caps text-text-secondary">
              Размер слота · миграция капитала
            </h3>
            <span
              className="text-helper-sm text-text-muted"
              data-testid="capital-scaling-auto"
              title="Правило платформы, одно для всех счетов: когда слоты заполнены, освободившийся слот получает +50 из свободного капитала, по одному за раз. Уже поднятые слоты не понижаются."
            >
              {capital.scaling.auto_scale === null
                ? "Рост +50 — не сообщается"
                : capital.scaling.auto_scale
                  ? "Рост +50 включён"
                  : "Рост +50 выключен"}
            </span>
          </div>
          <div className="grid min-w-0 grid-cols-2 gap-2 lg:grid-cols-4">
            <Stat
              label="Текущий переход"
              testId="capital-scaling-tier"
              value={
                capital.scaling.current_tier === null ||
                capital.scaling.next_tier === null
                  ? "—"
                  : `${money(capital.scaling.current_tier)} → ${money(capital.scaling.next_tier)}`
              }
            />
            <Stat
              label="Слотов повышено"
              testId="capital-scaling-upgraded"
              value={`${String(capital.scaling.upgraded_slots)} / ${String(capital.scaling.total_slots)}`}
            />
            <Stat
              label="Обеспечено капиталом"
              value={money(capital.scaling.funded_slot_capacity)}
            />
            <Stat
              label="Свободно для повышения"
              testId="capital-scaling-reinvestable"
              // Null until an authority composes it. A dash rather than a
              // figure: this screen does not work capital out for itself, and
              // a number invented here would be a second answer to how much
              // the account may commit.
              value={
                capital.scaling.reinvestable_free_capital === null
                  ? "—"
                  : money(capital.scaling.reinvestable_free_capital)
              }
              note={`шаг ${money(capital.scaling.next_upgrade_cost)}`}
            />
          </div>
        </section>
      )}

      <section
        className="flex flex-col gap-2"
        data-testid="capital-pools"
      >
        <h3
          className="text-label-caps text-text-secondary"
          title="Свободный слот получает пул, дальше всех отстающий от своей цели. Сколько слотов у пула — на его карточке."
        >
          Пулы стратегии
        </h3>
        <div className="grid min-w-0 grid-cols-1 gap-2 sm:grid-cols-3">
          {capital.pools.map((pool, index) => (
            <PoolCard key={pool.pool} pool={pool} index={index} />
          ))}
        </div>
      </section>

      {/* The same allocator the supervisor funds slots with, so a count here
          is one the strategy would actually open. */}
      <p className="text-helper-sm text-text-muted" data-testid="capital-pools-scope">
        Новый слот — {money(capital.target_slot_capital, 0)} USDT маржи. Под сделки идёт не больше
        {percent(capital.slot_share, 0)} капитала, остальное — резерв для второй сетки; каждый вход проверяется на падение
        рынка на 50 %. Прибыль, отложенная к выводу, в торговлю не идёт.
      </p>

      {capital.over_allocated && (
        <p
          data-testid="capital-over"
          className="flex items-start gap-1.5 text-helper-sm text-status-warning"
        >
          <CircleAlert aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span>
            Используемая маржа превышает лимит стратегии. Новые циклы не открываются;
            открытые продолжают сопровождаться.
          </span>
        </p>
      )}
    </div>
  );
}
